import React, { useEffect, useState } from 'react';
import Select from '@gik/ui/Select';
import { usePageCategories } from '@gik/homepage/api/featuredPageCategories';
import { FeaturedPage, useFeaturedPages } from '@gik/homepage/api/featuredPages';
import Slider, { Settings } from 'react-slick';
import twConfig from '@gik/core/tailwind.config';
import { InkindTile } from '@gik/ui/InkindTile/InkindTile';
import randomArray from '@gik/core/utils/randomArray';

export const HomepageSectionFeatured = ({ title = 'Featured Pages' }: { title?: string }) => {
  const MAX_PAGES = 4;

  const [slideSettings, setSlideSettings] = useState<Settings>(null);
  const [lowCountClass, setLowCountClass] = useState<string>('');
  const [pages, setPages] = useState<FeaturedPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hide, setHide] = useState<boolean>(false);
  const [category, setCategory] = useState<string>();

  const [pageCategories] = usePageCategories();
  const { data: pageResponse } = useFeaturedPages(category);

  useEffect(() => {
    if (!pageCategories) {
      return;
    }

    if (!pageCategories.length) {
      setHide(true);
    } else {
      setCategory(pageCategories[0].value);
    }
  }, [pageCategories]);

  const onCategorySelect = (selectedValue: string) => {
    if (selectedValue && category !== selectedValue) {
      setLoading(true);
      setCategory(selectedValue);
    }
  };

  useEffect(() => {
    if (!pageResponse) {
      return;
    }

    if (pageResponse.length) {
      setLowCountClass('');
      if (pageResponse?.length < MAX_PAGES) {
        setLowCountClass(`gik-homepage-featured--length-${pageResponse.length}`);
      }
      const pages = randomArray<FeaturedPage>(pageResponse, MAX_PAGES);
      setSlideSettings(buildSlideSettings(pages));
      setPages(pages);
      setLoading(false);
    } else {
      setHide(true);
    }
  }, [pageResponse]);

  const buildSlideSettings = (pages: FeaturedPage[]): Settings => {
    return {
      // 1025 +
      dots: true,
      variableWidth: true,
      speed: 800,
      // overriden
      slidesToShow: Math.min(pages.length, 4),
      slidesToScroll: Math.min(pages.length, 4),
      arrows: false,
      infinite: pages.length > 4,
      centerMode: pages.length < 2, // this does nothing
      responsive: [
        {
          breakpoint: parseInt(twConfig.theme.screens.md), // 0 - 768
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: true,
            infinite: true,
            centerMode: true,
          },
        },
        {
          breakpoint: parseInt(twConfig.theme.screens.lg), // 769 - 1024
          settings: {
            slidesToShow: Math.min(pages.length, 3),
            slidesToScroll: Math.min(pages.length, 3),
            arrows: pages.length > 3,
            infinite: pages.length > 3,
            centerMode: false,
          },
        },
      ],
    };
  };

  const handleClick = (page: FeaturedPage) => {
    const inkindPageUrl = `${process.env.BASE_URL}/inkinds/${page.routeId}/${
      page.slug
    }?origin=homepage&category=${encodeURIComponent(pageCategories.find(c => c.value === category).label)}`;

    window.location.href = inkindPageUrl;
  };

  const loadingInkindTiles = (
    <div className="gik-homepage-featured__loading-slides">
      {Array.from(Array(4).keys()).map(i => (
        <InkindTile key={i} loading={true} />
      ))}
    </div>
  );

  if (hide) {
    return null;
  }

  return (
    <div className="gik-homepage-featured">
      <div className="gik-homepage-featured__title">{title}</div>
      <div className="gik-homepage-featured__category-selection">
        {pageCategories ? (
          <Select
            instanceId="pageCategoriesSelect"
            options={pageCategories}
            isLoading={loading}
            placeholder={loading ? 'Loading Categories' : 'Please select a category'}
            value={category}
            onChange={onCategorySelect}
            className="gik-homepage-featured__select"
            variant="primary"
            menuPortalTarget={null}
          />
        ) : (
          <div className="gik-homepage-featured__select-placeholder" />
        )}
      </div>
      <div className={`gik-homepage-featured__slides ${lowCountClass}`}>
        {loading && !pages.length && loadingInkindTiles}
        {pages?.length > 0 && slideSettings && (
          <Slider {...slideSettings}>
            {pages?.map((page, index) => (
              <InkindTile
                onClick={() => handleClick(page)}
                key={index}
                title={page.title}
                thumbnail={page.imgSrc}
                location={page.location}
              />
            ))}
          </Slider>
        )}
      </div>
    </div>
  );
};
